import CryptoJS from 'crypto-js';
import { sha3_224, sha3_256, sha3_384, sha3_512 } from 'js-sha3';
import { blake2b, blake2s } from 'blakejs';
import { str as crc32 } from 'crc-32';
import { str as adler32 } from 'adler-32';
import murmurhash3 from 'murmurhash3js';
import { HashAlgorithm, EncodingFormat, HashOptions, HashResult } from '../types';

// Simple Base32 encoding (RFC 4648)
function base32Encode(data: Uint8Array): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = 0;
  let value = 0;
  let output = '';

  for (let i = 0; i < data.length; i++) {
    value = (value << 8) | data[i];
    bits += 8;

    while (bits >= 5) {
      output += alphabet[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }

  if (bits > 0) {
    output += alphabet[(value << (5 - bits)) & 31];
  }

  // Add padding
  while (output.length % 8 !== 0) {
    output += '=';
  }

  return output;
}

// Simple Base58 encoding (Bitcoin style)
function base58Encode(data: Uint8Array): string {
  const alphabet = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  if (data.length === 0) return '';

  // Convert to BigInt
  let num = BigInt(0);
  for (let i = 0; i < data.length; i++) {
    num = num * BigInt(256) + BigInt(data[i]);
  }

  if (num === BigInt(0)) return '1';

  let result = '';
  while (num > 0) {
    result = alphabet[Number(num % BigInt(58))] + result;
    num = num / BigInt(58);
  }

  // Add leading zeros
  for (let i = 0; i < data.length && data[i] === 0; i++) {
    result = '1' + result;
  }

  return result;
}

// Simple Base62 encoding
function base62Encode(data: Uint8Array): string {
  const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  if (data.length === 0) return '';

  let num = BigInt(0);
  for (let i = 0; i < data.length; i++) {
    num = num * BigInt(256) + BigInt(data[i]);
  }

  if (num === BigInt(0)) return '0';

  let result = '';
  while (num > 0) {
    result = alphabet[Number(num % BigInt(62))] + result;
    num = num / BigInt(62);
  }

  return result;
}

// Simple Base85 (ASCII85) encoding
function base85Encode(data: Uint8Array): string {
  const alphabet = '!"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstu';
  let output = '';
  
  for (let i = 0; i < data.length; i += 4) {
    let value = 0;
    const padding = Math.min(4, data.length - i);
    
    for (let j = 0; j < padding; j++) {
      value = (value << 8) | data[i + j];
    }
    
    // Pad with zeros if needed
    if (padding < 4) {
      value = value << (8 * (4 - padding));
    }
    
    // Encode 5 base85 digits
    const digits: number[] = [];
    for (let j = 0; j < 5; j++) {
      digits.push(value % 85);
      value = Math.floor(value / 85);
    }
    
    // Reverse and output (skip padding digits)
    const skipDigits = 4 - padding;
    for (let j = 4; j >= skipDigits; j--) {
      output += alphabet[digits[j]];
    }
  }
  
  return output;
}

// Initialize xxHash (will be loaded async)
let xxhashModule: any = null;

async function initXXHash() {
  if (!xxhashModule) {
    try {
      const xxhash = await import('xxhash-wasm');
      xxhashModule = await xxhash.default();
    } catch (error) {
      console.error('Failed to load xxhash-wasm:', error);
      throw new Error('xxHash library failed to load');
    }
  }
  return xxhashModule;
}

// WebCrypto API for SHA algorithms
// Only supports: SHA-1, SHA-256, SHA-384, SHA-512
async function hashWithWebCrypto(data: Uint8Array, algorithm: string): Promise<ArrayBuffer> {
  // WebCrypto API requires exact algorithm names with hyphens
  const supportedAlgorithms: Record<string, string> = {
    'sha1': 'SHA-1',
    'sha256': 'SHA-256',
    'sha384': 'SHA-384',
    'sha512': 'SHA-512',
  };
  
  const normalized = algorithm.toLowerCase().replace(/-/g, '');
  const webCryptoAlgo = supportedAlgorithms[normalized];
  
  if (!webCryptoAlgo) {
    throw new Error(`Algorithm ${algorithm} is not supported by WebCrypto API`);
  }
  
  // Ensure we have a proper ArrayBuffer
  const buffer = ensureArrayBuffer(data.buffer);
  const view = new Uint8Array(buffer, data.byteOffset, data.byteLength);
  
  return await crypto.subtle.digest(webCryptoAlgo, view);
}

// Helper to ensure we have an ArrayBuffer (not SharedArrayBuffer)
function ensureArrayBuffer(buffer: ArrayBufferLike): ArrayBuffer {
  if (buffer instanceof ArrayBuffer) {
    return buffer;
  }
  // If it's a SharedArrayBuffer, create a new ArrayBuffer copy
  const view = new Uint8Array(buffer);
  const copy = new Uint8Array(view.length);
  copy.set(view);
  return copy.buffer;
}

// Convert ArrayBuffer to hex
function arrayBufferToHex(buffer: ArrayBufferLike, uppercase = false): string {
  const ab = ensureArrayBuffer(buffer);
  const hex = Array.from(new Uint8Array(ab))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return uppercase ? hex.toUpperCase() : hex;
}

// Convert string to Uint8Array
function stringToUint8Array(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

// Convert Uint8Array to string
function uint8ArrayToString(arr: Uint8Array): string {
  return new TextDecoder().decode(arr);
}

// Encoding functions
function encodeHash(hash: string | Uint8Array | ArrayBuffer, encoding: EncodingFormat, options?: HashOptions): string {
  let data: Uint8Array;
  
  if (hash instanceof ArrayBuffer) {
    data = new Uint8Array(hash);
  } else if (hash instanceof Uint8Array) {
    data = hash;
  } else {
    // Assume hex string (from CryptoJS or other string-based hashers)
    // If it's already in the desired encoding, return as-is for hex
    if (encoding === 'hex' || encoding === 'hex-upper') {
      const uppercase = options?.outputCase === 'upper' || encoding === 'hex-upper';
      return uppercase ? hash.toUpperCase() : hash.toLowerCase();
    }
    // Convert hex string to bytes for other encodings
    const bytes = new Uint8Array(hash.length / 2);
    for (let i = 0; i < hash.length; i += 2) {
      bytes[i / 2] = parseInt(hash.substr(i, 2), 16);
    }
    data = bytes;
  }

  const uppercase = options?.outputCase === 'upper';

  switch (encoding) {
    case 'hex':
      return arrayBufferToHex(data.buffer, uppercase);
    case 'hex-upper':
      return arrayBufferToHex(data.buffer, true);
    case 'base64': {
      // Handle large arrays
      if (data.length > 10000) {
        let binary = '';
        for (let i = 0; i < data.length; i += 8192) {
          binary += String.fromCharCode(...data.slice(i, i + 8192));
        }
        return btoa(binary);
      }
      return btoa(String.fromCharCode(...data));
    }
    case 'base32':
      try {
        return base32Encode(data);
      } catch {
        return arrayBufferToHex(data.buffer, uppercase);
      }
    case 'base58':
      try {
        return base58Encode(data);
      } catch {
        return arrayBufferToHex(data.buffer, uppercase);
      }
    case 'base62':
      try {
        return base62Encode(data);
      } catch {
        return arrayBufferToHex(data.buffer, uppercase);
      }
    case 'base85':
      try {
        return base85Encode(data);
      } catch {
        return arrayBufferToHex(data.buffer, uppercase);
      }
    case 'binary':
      return Array.from(data).map(b => b.toString(2).padStart(8, '0')).join(' ');
    default:
      return arrayBufferToHex(data.buffer, uppercase);
  }
}

// Hash computation functions
export async function computeHash(
  input: string | File,
  algorithm: HashAlgorithm,
  encoding: EncodingFormat = 'hex',
  options?: HashOptions
): Promise<HashResult> {
  const startTime = performance.now();
  let hash: string | Uint8Array | ArrayBuffer;
  let inputLength: number;
  let inputType: 'text' | 'file';
  let fileName: string | undefined;

  if (input instanceof File) {
    inputType = 'file';
    fileName = input.name;
    const arrayBuffer = await input.arrayBuffer();
    inputLength = arrayBuffer.byteLength;
    const data = new Uint8Array(arrayBuffer);
    
    // Handle HMAC algorithms
    if (algorithm.startsWith('HMAC-')) {
      const baseAlgo = algorithm.replace('HMAC-', '');
      if (!options?.key) {
        throw new Error('HMAC requires a key');
      }
      hash = await computeHMAC(data, baseAlgo, options.key);
    } else {
      hash = await computeHashForData(data, algorithm);
    }
  } else {
    inputType = 'text';
    inputLength = new TextEncoder().encode(input).length;
    const data = stringToUint8Array(input);
    
    if (algorithm.startsWith('HMAC-')) {
      const baseAlgo = algorithm.replace('HMAC-', '');
      if (!options?.key) {
        throw new Error('HMAC requires a key');
      }
      hash = await computeHMAC(data, baseAlgo, options.key);
    } else {
      hash = await computeHashForData(data, algorithm);
    }
  }

  const durationMs = performance.now() - startTime;
  const encodedHash = encodeHash(hash, encoding, options);

  return {
    algorithm,
    encoding,
    hash: encodedHash,
    durationMs,
    inputLength,
    inputType,
    fileName,
  };
}

async function computeHashForData(data: Uint8Array, algorithm: HashAlgorithm): Promise<string | Uint8Array | ArrayBuffer> {
  switch (algorithm) {
    // WebCrypto supported algorithms (SHA-224 is NOT supported by WebCrypto, use CryptoJS)
    case 'SHA-1':
    case 'SHA-256':
    case 'SHA-384':
    case 'SHA-512':
      const algo = algorithm.toLowerCase().replace(/-/g, '');
      return await hashWithWebCrypto(data, algo);

    case 'SHA-224':
      // SHA-224 is not supported by WebCrypto API, use CryptoJS
      return CryptoJS.SHA224(uint8ArrayToString(data)).toString();

    case 'SHA-512/224':
      // SHA-512/224 is SHA-512 truncated to 224 bits
      const sha512_224 = await hashWithWebCrypto(data, 'sha512');
      return sha512_224.slice(0, 28); // 224 bits = 28 bytes

    case 'SHA-512/256':
      // SHA-512/256 is SHA-512 truncated to 256 bits
      const sha512_256 = await hashWithWebCrypto(data, 'sha512');
      return sha512_256.slice(0, 32); // 256 bits = 32 bytes

    // SHA-3 family
    case 'SHA3-224':
      return sha3_224.arrayBuffer(uint8ArrayToString(data));
    case 'SHA3-256':
      return sha3_256.arrayBuffer(uint8ArrayToString(data));
    case 'SHA3-384':
      return sha3_384.arrayBuffer(uint8ArrayToString(data));
    case 'SHA3-512':
      return sha3_512.arrayBuffer(uint8ArrayToString(data));

    // BLAKE family
    case 'BLAKE2b':
      return blake2b(data, undefined, 64);
    case 'BLAKE2s':
      return blake2s(data, undefined, 32);
    case 'BLAKE3':
      // BLAKE3 requires WASM, fallback to BLAKE2b for now
      // In production, use blake3-wasm
      return blake2b(data, undefined, 64);

    // CryptoJS algorithms
    case 'MD5':
      return CryptoJS.MD5(uint8ArrayToString(data)).toString();
    case 'RIPEMD-160':
      return CryptoJS.RIPEMD160(uint8ArrayToString(data)).toString();
    case 'Whirlpool':
      // Whirlpool may not be available in all CryptoJS builds, fallback to SHA-256
      try {
        return (CryptoJS as any).Whirlpool(uint8ArrayToString(data)).toString();
      } catch {
        return CryptoJS.SHA256(uint8ArrayToString(data)).toString();
      }
    case 'Tiger':
      // Tiger may not be available in all CryptoJS builds, fallback to SHA-256
      try {
        return (CryptoJS as any).Tiger(uint8ArrayToString(data)).toString();
      } catch {
        return CryptoJS.SHA256(uint8ArrayToString(data)).toString();
      }

    // Checksum algorithms
    case 'CRC32':
      return crc32(uint8ArrayToString(data)).toString(16).padStart(8, '0');
    case 'Adler-32':
      return adler32(uint8ArrayToString(data)).toString(16).padStart(8, '0');
    case 'MurmurHash3': {
      const hash = murmurhash3.x86.hash32(uint8ArrayToString(data));
      return hash.toString(16).padStart(8, '0');
    }

    // xxHash
    case 'xxHash32': {
      try {
        const xxhash = await initXXHash();
        // xxhash-wasm API: h32() returns a hasher, update() takes Uint8Array, digest() returns number
        if (!xxhash || typeof xxhash.h32 !== 'function') {
          throw new Error('xxHash module not properly loaded');
        }
        const hash32 = xxhash.h32();
        if (!hash32 || typeof hash32.update !== 'function') {
          throw new Error('xxHash h32() did not return valid hasher');
        }
        // Ensure data is a proper Uint8Array
        const dataArray = data instanceof Uint8Array ? data : new Uint8Array(data);
        hash32.update(dataArray);
        const result = hash32.digest();
        if (result === undefined || result === null) {
          throw new Error('xxHash digest() returned undefined');
        }
        // Convert number to hex string
        const hex = typeof result === 'number' 
          ? result.toString(16).padStart(8, '0')
          : result.toString(16).padStart(8, '0');
        return hex;
      } catch (error) {
        // Fallback to MurmurHash3 if xxHash fails
        console.warn('xxHash32 failed, using MurmurHash3 fallback:', error);
        const hash = murmurhash3.x86.hash32(uint8ArrayToString(data));
        return hash.toString(16).padStart(8, '0');
      }
    }
    case 'xxHash64': {
      try {
        const xxhash64 = await initXXHash();
        // xxhash-wasm API: h64() returns a hasher, update() takes Uint8Array, digest() returns bigint
        if (!xxhash64 || typeof xxhash64.h64 !== 'function') {
          throw new Error('xxHash module not properly loaded');
        }
        const hash64 = xxhash64.h64();
        if (!hash64 || typeof hash64.update !== 'function') {
          throw new Error('xxHash h64() did not return valid hasher');
        }
        // Ensure data is a proper Uint8Array
        const dataArray = data instanceof Uint8Array ? data : new Uint8Array(data);
        hash64.update(dataArray);
        const result = hash64.digest();
        if (result === undefined || result === null) {
          throw new Error('xxHash digest() returned undefined');
        }
        // Convert bigint or number to hex string
        const hex = typeof result === 'bigint' || typeof result === 'number'
          ? result.toString(16).padStart(16, '0')
          : String(result).padStart(16, '0');
        return hex;
      } catch (error) {
        // Fallback to MurmurHash3 if xxHash fails
        console.warn('xxHash64 failed, using MurmurHash3 fallback:', error);
        const hash = murmurhash3.x86.hash32(uint8ArrayToString(data));
        return hash.toString(16).padStart(16, '0');
      }
    }

    // SipHash (simplified, requires proper implementation)
    case 'SipHash': {
      // Fallback to MurmurHash3 for now
      const hash = murmurhash3.x86.hash32(uint8ArrayToString(data));
      return hash.toString(16).padStart(16, '0');
    }

    default:
      throw new Error(`Unsupported algorithm: ${algorithm}`);
  }
}

async function computeHMAC(data: Uint8Array, algorithm: string, key: string): Promise<ArrayBuffer> {
  const keyData = stringToUint8Array(key);
  
  // WebCrypto API requires exact algorithm names with hyphens for HMAC
  const supportedAlgorithms: Record<string, string> = {
    'md5': 'SHA-1', // MD5 not supported, fallback to SHA-1 (or use CryptoJS)
    'sha1': 'SHA-1',
    'sha256': 'SHA-256',
    'sha384': 'SHA-384',
    'sha512': 'SHA-512',
  };
  
  const normalized = algorithm.toLowerCase().replace(/-/g, '');
  let hashAlgo = supportedAlgorithms[normalized];
  
  // If MD5, use CryptoJS instead (WebCrypto doesn't support MD5)
  if (normalized === 'md5') {
    const hmac = CryptoJS.HmacMD5(uint8ArrayToString(data), key);
    const hex = hmac.toString();
    // Convert hex string to ArrayBuffer
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
  }
  
  if (!hashAlgo) {
    // Fallback to SHA-256 if algorithm not supported
    hashAlgo = 'SHA-256';
  }
  
  // Ensure we have a proper ArrayBuffer for key
  const keyBuffer = ensureArrayBuffer(keyData.buffer);
  const keyView = new Uint8Array(keyBuffer, keyData.byteOffset, keyData.byteLength);
  
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyView,
    { name: 'HMAC', hash: hashAlgo },
    false,
    ['sign']
  );

  // Ensure we have a proper ArrayBuffer for data
  const dataBuffer = ensureArrayBuffer(data.buffer);
  const dataView = new Uint8Array(dataBuffer, data.byteOffset, data.byteLength);

  return await crypto.subtle.sign('HMAC', cryptoKey, dataView);
}

// Verify hash
export async function verifyHash(
  input: string | File,
  algorithm: HashAlgorithm,
  expectedHash: string,
  encoding: EncodingFormat = 'hex',
  options?: HashOptions
): Promise<{ match: boolean; computedHash: string; expectedHash: string; algorithm: HashAlgorithm; encoding: EncodingFormat }> {
  const result = await computeHash(input, algorithm, encoding, options);
  const match = result.hash.toLowerCase() === expectedHash.toLowerCase();
  
  return {
    match,
    computedHash: result.hash,
    expectedHash,
    algorithm,
    encoding,
  };
}

